import { css } from "styled-system/css";
import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, UserPlus, FileText, ChevronRight, Trash2, Settings2 } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale/es";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { cf } from "@/components/brand/sfturnos-styles";
import { DashboardPageShell } from "@/components/dashboard/dashboard-page-shell";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { Button } from "@/components/ui/button";
import { NewOdontogramRecordModal, type OdontogramClientPrefill } from "@/components/odontogram/new-odontogram-record-modal";
import { useConfirm } from "@/hooks/useConfirm";

type RecordRow = {
  id: string;
  clientId: string;
  clientName: string;
  clientEmail: string | null;
  clientPhone: string | null;
  lastConsultDate: string | null;
  consultsCount: number;
  updatedAt: string;
};

function initials(name: string): string {
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

function avatarBg(name: string): string {
  const colors = [
    "linear-gradient(135deg,#6366f1,#818cf8)",
    "linear-gradient(135deg,#8b5cf6,#a78bfa)",
    "linear-gradient(135deg,#0ea5e9,#38bdf8)",
    "linear-gradient(135deg,#10b981,#34d399)",
    "linear-gradient(135deg,#f59e0b,#fbbf24)",
    "linear-gradient(135deg,#ef4444,#f87171)",
  ];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return colors[Math.abs(h) % colors.length];
}

export function DashboardOdontogram() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const confirm = useConfirm();
  const [rows, setRows] = useState<RecordRow[] | null>(null);
  const [search, setSearch] = useState("");
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [newModalPrefill, setNewModalPrefill] = useState<OdontogramClientPrefill | null>(null);
  const [noAccess, setNoAccess] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Llegada desde una cita/cliente sin historia clínica todavía (OdontogramByClient/ByEmail):
  // abrir el modal de "Nueva historia" ya prellenado en vez de mandar a una lista vacía.
  useEffect(() => {
    const state = location.state as { openNewFor?: OdontogramClientPrefill } | null;
    if (!state?.openNewFor) return;
    setNewModalPrefill(state.openNewFor);
    setNewModalOpen(true);
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(
    async (q?: string) => {
      const qs = q != null ? `?q=${encodeURIComponent(q)}` : "";
      const r = await fetchWithSessionSync(
        `/api/dashboard/odontogram/records${qs}`,
        { credentials: "include" },
        refresh,
      );
      if (r.status === 403) {
        setNoAccess(true);
        return;
      }
      if (!r.ok) return;
      const data = (await r.json()) as { records: RecordRow[] };
      setRows(data.records);
    },
    [refresh],
  );

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => { void load(search); }, 280);
    return () => clearTimeout(t);
  }, [search, load]);

  async function deleteRecord(row: RecordRow) {
    const consultsLabel = row.consultsCount === 1 ? "1 consulta guardada" : `${row.consultsCount} consultas guardadas`;
    const confirmMsg =
      row.consultsCount > 0
        ? `"${row.clientName}" tiene ${consultsLabel}. No se puede eliminar la historia hasta borrar todas las consultas una a una.`
        : `¿Eliminar la historia clínica de "${row.clientName}"? Esta acción no se puede deshacer.`;

    if (row.consultsCount > 0) {
      setDeleteError(confirmMsg);
      return;
    }

    if (!(await confirm({ message: confirmMsg, tone: "danger", confirmLabel: "Eliminar" }))) return;

    setDeletingId(row.id);
    setDeleteError(null);
    const r = await fetchWithSessionSync(
      `/api/dashboard/odontogram/records/${row.id}`,
      { method: "DELETE", credentials: "include" },
      refresh,
    );
    setDeletingId(null);
    if (!r.ok) {
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      setDeleteError(j.error ?? "No se pudo eliminar");
      return;
    }
    void load(search);
  }

  if (noAccess) {
    return (
      <DashboardPageShell eyebrow="Odontograma" title="Odontograma + Historia clínica">
        <div
          className={css({
            display: "flex",
            flexDir: "column",
            alignItems: "center",
            justifyContent: "center",
            py: "16",
            gap: "4",
            textAlign: "center",
          })}
        >
          <FileText size={48} strokeWidth={1.2} aria-hidden className={css({ color: "#94a3b8" })} />
          <div>
            <p className={css({ fontSize: "md", fontWeight: "semibold", color: "#0f172a", mb: "1" })}>
              Acceso requerido
            </p>
            <p className={css({ fontSize: "sm", color: "#64748b", maxW: "340px" })}>
              Este módulo requiere tener activa la feature Odontograma en el Market.
            </p>
          </div>
          <Link
            to="/dashboard/market"
            className={css({
              fontSize: "sm",
              fontWeight: "semibold",
              color: "white",
              bg: "token(colors.brand.600)",
              px: "4",
              py: "2",
              rounded: "lg",
              textDecoration: "none",
              transition: "opacity 120ms ease",
              _hover: { opacity: 0.88 },
            })}
          >
            Ir al Market
          </Link>
        </div>
      </DashboardPageShell>
    );
  }

  return (
    <>
      <DashboardPageShell
        eyebrow="Odontograma"
        title="Historia clínica"
        description="Gestión de odontogramas e historia clínica por paciente."
        actions={
          <div className={css({ display: "flex", alignItems: "center", gap: "2" })}>
            <Link
              to="/dashboard/settings/custom-fields"
              title="Personalizar formularios"
              aria-label="Personalizar formularios"
              className={css({
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                w: "9",
                h: "9",
                flexShrink: 0,
                rounded: "lg",
                borderWidth: "1px",
                borderColor: "#e2e8f0",
                bg: "white",
                color: "#475569",
                cursor: "pointer",
                transition: "transform 160ms cubic-bezier(0.23, 1, 0.32, 1), background 120ms ease",
                _hover: { bg: "#f8fafc" },
                _active: { transform: "scale(0.94)" },
              })}
            >
              <Settings2 size={16} aria-hidden />
            </Link>
            <Button size="sm" onClick={() => setNewModalOpen(true)}>
              <UserPlus size={14} aria-hidden />
              Nueva historia
            </Button>
          </div>
        }
      >
        {rows === null ? (
          <LoadingFrame variant="light" minHeight={240} />
        ) : (
          <>
            <div
              className={css({
                display: "flex",
                alignItems: "center",
                gap: "2",
                px: "3",
                py: "2",
                rounded: "lg",
                borderWidth: "1px",
                borderColor: "#e2e8f0",
                bg: "white",
                maxW: "400px",
              })}
            >
              <Search size={14} className={css({ color: "#94a3b8", flexShrink: 0 })} aria-hidden />
              <input
                type="search"
                placeholder="Buscar paciente…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={css({
                  flex: 1,
                  fontSize: "sm",
                  color: "#0f172a",
                  bg: "transparent",
                  border: "none",
                  outline: "none",
                  _placeholder: { color: "#94a3b8" },
                })}
              />
            </div>

            {rows.length === 0 ? (
              <div
                className={css({
                  display: "flex",
                  flexDir: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: "16",
                  gap: "3",
                  color: "#94a3b8",
                })}
              >
                <FileText size={40} strokeWidth={1.25} aria-hidden />
                <p className={css({ fontSize: "sm" })}>
                  {search ? "No se encontraron pacientes." : "Todavía no hay historias clínicas."}
                </p>
                {!search && (
                  <Button size="sm" variant="secondary" onClick={() => setNewModalOpen(true)}>
                    Crear primera historia
                  </Button>
                )}
              </div>
            ) : (
              <div
                className={css({
                  bg: "white",
                  rounded: "xl",
                  borderWidth: "1px",
                  borderColor: "#e2e8f0",
                  overflow: "hidden",
                })}
              >
                {rows.map((row, i) => (
                  <div
                    key={row.id}
                    className={css({
                      w: "full",
                      display: "flex",
                      alignItems: "center",
                      gap: "3",
                      px: "4",
                      py: "3.5",
                      bg: "white",
                      borderTopWidth: i > 0 ? "1px" : "0",
                      borderColor: "#f1f5f9",
                      transition: "background 100ms ease",
                      _hover: { bg: "#f8fafc" },
                    })}
                  >
                    {/* Zona clickeable principal */}
                    <button
                      type="button"
                      onClick={() => navigate(`/dashboard/odontogram/${row.id}`)}
                      className={css({
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: "3",
                        minW: 0,
                        textAlign: "left",
                        bg: "transparent",
                        border: "none",
                        cursor: "pointer",
                        p: "0",
                      })}
                    >
                      <div
                        className={css({
                          w: "9",
                          h: "9",
                          rounded: "full",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "xs",
                          fontWeight: "bold",
                          color: "white",
                          lineHeight: 1,
                        })}
                        style={{ background: avatarBg(row.clientName) }}
                      >
                        {initials(row.clientName)}
                      </div>

                      <div className={css({ flex: 1, minW: 0 })}>
                        <p className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a", truncate: true })}>
                          {row.clientName}
                        </p>
                        <p className={css({ fontSize: "xs", color: "#94a3b8", mt: "0.5", truncate: true })}>
                          {row.clientEmail ? row.clientEmail : ""}
                          {row.clientPhone ? (row.clientEmail ? ` · ${row.clientPhone}` : row.clientPhone) : ""}
                        </p>
                      </div>

                      <div className={css({ textAlign: "right", flexShrink: 0 })}>
                        {row.lastConsultDate ? (
                          <p className={css({ fontSize: "xs", color: "#64748b" })}>
                            {format(new Date(row.lastConsultDate), "d MMM yyyy", { locale: es })}
                          </p>
                        ) : null}
                        <p className={css({ fontSize: "xs", color: "#94a3b8", mt: "0.5" })}>
                          {row.consultsCount === 0
                            ? "Sin consultas"
                            : row.consultsCount === 1
                            ? "1 consulta"
                            : `${row.consultsCount} consultas`}
                        </p>
                      </div>

                      <ChevronRight
                        size={14}
                        className={css({ color: "#cbd5e1", flexShrink: 0 })}
                        aria-hidden
                      />
                    </button>

                    {/* Botón eliminar */}
                    <button
                      type="button"
                      disabled={deletingId === row.id}
                      onClick={() => void deleteRecord(row)}
                      aria-label={`Eliminar historia de ${row.clientName}`}
                      className={css({
                        flexShrink: 0,
                        p: "1.5",
                        rounded: "md",
                        border: "none",
                        bg: "transparent",
                        cursor: "pointer",
                        color: "#94a3b8",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "color 120ms ease, opacity 120ms ease",
                        _hover: { color: "#ef4444" },
                        _active: { transform: "scale(0.93)" },
                        _disabled: { opacity: 0.4, cursor: "not-allowed" },
                      })}
                    >
                      <Trash2 size={14} aria-hidden />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {deleteError && (
              <div
                className={css({
                  mt: "3",
                  px: "4",
                  py: "3",
                  rounded: "lg",
                  bg: "#fef2f2",
                  borderWidth: "1px",
                  borderColor: "#fecaca",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: "3",
                })}
              >
                <p className={css({ fontSize: "sm", color: "#b91c1c" })}>{deleteError}</p>
                <button
                  type="button"
                  onClick={() => setDeleteError(null)}
                  className={css({
                    flexShrink: 0,
                    fontSize: "xs",
                    color: "#b91c1c",
                    bg: "transparent",
                    border: "none",
                    cursor: "pointer",
                    opacity: 0.7,
                    _hover: { opacity: 1 },
                  })}
                >
                  Cerrar
                </button>
              </div>
            )}
          </>
        )}
      </DashboardPageShell>

      <NewOdontogramRecordModal
        open={newModalOpen}
        onClose={() => { setNewModalOpen(false); setNewModalPrefill(null); }}
        onCreated={(recordId) => {
          setNewModalOpen(false);
          setNewModalPrefill(null);
          navigate(`/dashboard/odontogram/${recordId}`);
        }}
        prefill={newModalPrefill}
        onPrefillConsumed={() => setNewModalPrefill(null)}
      />
    </>
  );
}
